import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ChevronRight, Plus } from "lucide-react";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import { SensorSuggestionIcon } from "./SensorSuggestionIcon";

interface SensorSuggestionProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  totalSensors: number;
  suggestedSensors: ISensorMeasuredData[];
  radiusKm: number;
  hasSuggestion: boolean;
  onViewNearbySensors: () => void;
  onCreateMyOwnWay: () => void;
}

export const SensorSuggestion: React.FC<SensorSuggestionProps> = ({
  t,
  totalSensors,
  suggestedSensors,
  radiusKm,
  hasSuggestion,
  onViewNearbySensors,
  onCreateMyOwnWay,
}) => {
  // When there's no suggestion (no sensors in radius), show only "Create my own way"
  if (!hasSuggestion) {
    return (
      <Flex direction="column" align="center" gap="16px" py="8px">
        <Text
          fontSize="14px"
          lineHeight="20px"
          color="#4A6075"
          textAlign="center"
          maxW="500px"
        >
          {t("noSuggestionFound")}{" "}
          <Text as="span" fontWeight="700">
            {t("createAlertOutsideLocation")}
          </Text>
        </Text>
        <Box
          as="button"
          onClick={onCreateMyOwnWay}
          border="1px solid #B9C6D3"
          borderRadius="8px"
          w="242px"
          h="100px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="10px"
          cursor="pointer"
          _hover={{ borderColor: "#086AA9", bg: "rgba(8, 106, 169, 0.04)" }}
          transition="all 0.2s"
        >
          <Plus size={24} color="#4A6075" />
          <Text
            fontWeight="500"
            fontSize="14px"
            color="#4A6075"
            textAlign="center"
          >
            {t("createAlert")}
          </Text>
        </Box>
      </Flex>
    );
  }

  // Normal initial state with suggestion
  return (
    <Flex direction="column" align="center" gap="16px">
      {/* Suggestion description */}
      <Text
        fontSize="14px"
        lineHeight="20px"
        color="#4A6075"
        textAlign="center"
        dangerouslySetInnerHTML={{
          __html: t("suggestionFoundDescription", { count: totalSensors }),
        }}
      />

      {/* Action cards */}
      <Flex gap="12px" align="start">
        {/* Ver sensores próximos card — single clickable element (FR-008) */}
        <Box
          as="button"
          onClick={onViewNearbySensors}
          w="242px"
          border="1px solid #B9C6D3"
          borderRadius="8px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="8px"
          p="10px"
          cursor="pointer"
          _hover={{ borderColor: "#086AA9", bg: "rgba(8, 106, 169, 0.04)" }}
          transition="all 0.2s"
        >
          <Flex direction="column" align="center" gap="10px">
            <SensorSuggestionIcon size={20} color="#4A6075" />
            <Text
              fontWeight="500"
              fontSize="14px"
              color="#4A6075"
              textAlign="center"
            >
              {t("viewNearbySensors")}
            </Text>
          </Flex>
          <Flex align="center" gap="8px">
            <Text
              fontSize="10px"
              fontWeight="600"
              color="#0884CC"
              textTransform="uppercase"
              letterSpacing="1px"
              lineHeight="1.5"
            >
              {t("suggestionCardSubtitle", {
                count: suggestedSensors.length,
                radius: radiusKm,
              })}
            </Text>
            <ChevronRight size={16} color="#0884CC" />
          </Flex>
        </Box>

        {/* Create my own way card */}
        <Box
          as="button"
          onClick={onCreateMyOwnWay}
          w="242px"
          h="100px"
          border="1px solid #B9C6D3"
          borderRadius="8px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="10px"
          cursor="pointer"
          _hover={{ borderColor: "#086AA9", bg: "rgba(8, 106, 169, 0.04)" }}
          transition="all 0.2s"
        >
          <Plus size={24} color="#4A6075" />
          <Text
            fontWeight="500"
            fontSize="14px"
            color="#4A6075"
            textAlign="center"
          >
            {t("createMyOwnWay")}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};
